import { create } from 'zustand';
import omit from 'lodash-es/omit';
import cloneDeep from 'lodash-es/cloneDeep';
import { type PageReturn, type PageReturnLayout } from 'app/apis/page';

export type Page = Omit<PageReturn, 'layouts'>;

export interface UseShopPageState {
    /* 
        pageMap = {
            [page_id]: {
                id: string;
                name: string;
                custom_name: string;
                enabled: boolean;
            }
        }
    */
    pageMap: Record<Page['id'], Page>;

    /* 
        pageLayoutMap = {
            [page_id]: {
                [header | footer | body]: layout_id
            }
        }
    */
    pageLayoutMap: Record<
        Page['id'],
        Record<PageReturnLayout['section'], PageReturnLayout['id']>
    >;

    currentPageId: string | null;
}

export interface UseShopPageActions {
    addPage: (page: PageReturn) => void;
    removePage: (pageId: string) => void;
    setCurrentPageId: (pageId: string) => void;
    syncPage: (pages: PageReturn[]) => void;
    setPageLayout: (pageId: string, sectionName: string, layoutId: string) => void;
}

export const useShopPageStore = create<UseShopPageState & UseShopPageActions>(
    (set, get) => ({
        currentPageId: null,
        pageMap: {},
        pageLayoutMap: {},
        setPageLayout: (pageId: string, sectionName: string, layoutId: string) => {
            const { pageLayoutMap } = get();

            const newPageLayoutMap = { ...pageLayoutMap };

            newPageLayoutMap[pageId] = {
                ...newPageLayoutMap[pageId],
                [sectionName]: layoutId,
            };

            return set({ pageLayoutMap: newPageLayoutMap });
        },
        addPage: (page: PageReturn) => {
            const { pageMap: _pageMap, setPageLayout } = get();

            const newPage = omit(page, 'layouts');

            const pageMap = cloneDeep(_pageMap);

            pageMap[newPage.id] = newPage;

            for (const layout of page.layouts) {
                setPageLayout(newPage.id, layout.section, layout.id);
            }

            return set({ pageMap });
        },
        removePage: (pageId) => {
            const { pageMap: _pageMap, pageLayoutMap: _pageLayoutMap } = get();

            const pageMap = omit(_pageMap, pageId);

            const pageLayoutMap = omit(_pageLayoutMap, pageId);

            return set({ pageMap, pageLayoutMap });
        },
        setCurrentPageId: (pageId) => {
            const { pageMap } = get();

            if (!pageMap[pageId]) {
                return set({ currentPageId: null });
            }

            return set({ currentPageId: pageId });
        },
        syncPage: (pages) => {
            pages.forEach((page) => {
                get().addPage(page);
            });
        },
    })
);
